import { Autocomplete, Box, InputLabel, Stack, TextField } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { useEditMode } from 'contexts/edit-mode.context'
import FormSingleColumnLayout from '../../../layouts/form-single-column-layout/form-single-column-layout'
import TextInputField from '../../fields/text-input-field/text-input-field.component'
import {
  EDITABLE_VALUE_COLOR,
  READ_ONLY_VALUE_COLOR,
  readOnlyAutocompleteSx,
  readOnlyTextFieldSx,
} from '../../fields/field-read-only.styles'
import { LoadConfirmFormContainer } from '../forms.styles'

export type CompanySuggestion = {
  id: string
  companyName: string
  address: string
  billingEmail: string
  notificationEmail: string
  accountPayableEmail: string
  phone: string
}

type LoadCustomerFormProps = {
  companySuggestions?: CompanySuggestion[]
  companySuggestionsLoading?: boolean
  onCompanySelected?: (company: CompanySuggestion | null) => void
  displayVariant?: 'default' | 'orderDetails'
}

const COMPANY_AUTO_FILL_FIELDS = [
  'address',
  'billingEmail',
  'notificationEmail',
  'accountPayableEmail',
  'phone',
] as const

const orderDetailsFieldSx = {
  display: 'grid',
  gridTemplateColumns: '168px minmax(0, 1fr)',
  columnGap: 2.5,
  alignItems: 'center',
  width: '100%',
} as const

const orderDetailsLabelSx = {
  color: 'text.secondary',
  fontWeight: 500,
  fontSize: '0.875rem',
  lineHeight: 1.35,
  whiteSpace: 'normal',
} as const

const LoadCustomerForm = ({
  companySuggestions = [],
  companySuggestionsLoading = false,
  onCompanySelected,
  displayVariant = 'default',
}: LoadCustomerFormProps) => {
  const { control, setValue } = useFormContext()
  const { isEditMode, hasContext } = useEditMode()
  const isReadOnly = hasContext && !isEditMode
  const valueColor = isReadOnly ? READ_ONLY_VALUE_COLOR : EDITABLE_VALUE_COLOR
  const isOrderDetails = displayVariant === 'orderDetails'
  const customerNameFieldSx = isReadOnly
    ? readOnlyTextFieldSx
    : {
        '& .MuiInputBase-input': {
          color: valueColor,
        },
        '& .MuiInputBase-input.Mui-disabled': {
          WebkitTextFillColor: valueColor,
          color: valueColor,
        },
      }

  const clearCompanyAutoFields = () => {
    COMPANY_AUTO_FILL_FIELDS.forEach((field) => {
      setValue(field, '', { shouldDirty: true })
    })
  }

  const applyCompanyAutoFields = (company: CompanySuggestion) => {
    setValue('address', company.address, { shouldDirty: true })
    setValue('billingEmail', company.billingEmail, { shouldDirty: true })
    setValue('notificationEmail', company.notificationEmail, { shouldDirty: true })
    setValue('accountPayableEmail', company.accountPayableEmail, {
      shouldDirty: true,
    })
    setValue('phone', company.phone, { shouldDirty: true })
  }

  const handleCompanyCleared = () => {
    clearCompanyAutoFields()
    onCompanySelected?.(null)
  }

  const handleCompanyChosen = (company: CompanySuggestion) => {
    applyCompanyAutoFields(company)
    onCompanySelected?.(company)
  }

  if (isOrderDetails) {
    return (
      <Box sx={{ width: '100%', maxWidth: 760 }}>
        <Stack spacing={2.5}>
          <Box sx={orderDetailsFieldSx}>
            <InputLabel sx={orderDetailsLabelSx}>Customer Name</InputLabel>
            <Controller
              name="customerName"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Autocomplete
                  freeSolo
                  disabled={isReadOnly}
                  disableClearable={isReadOnly}
                  options={isReadOnly ? [] : companySuggestions}
                  loading={!isReadOnly && companySuggestionsLoading}
                  value={null}
                  inputValue={value || ''}
                  sx={isReadOnly ? readOnlyAutocompleteSx : undefined}
                  onInputChange={(_, inputValue, reason) => {
                    if (isReadOnly || reason === 'reset') return
                    onChange(inputValue)
                    if (reason === 'clear' || inputValue === '') {
                      handleCompanyCleared()
                      return
                    }
                    if (reason === 'input') {
                      handleCompanyCleared()
                    }
                  }}
                  onChange={(_, option) => {
                    if (isReadOnly) return
                    if (!option || typeof option === 'string') {
                      onChange(typeof option === 'string' ? option : '')
                      if (!option) {
                        handleCompanyCleared()
                      }
                      return
                    }
                    onChange(option.companyName)
                    handleCompanyChosen(option)
                  }}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.companyName
                  }
                  isOptionEqualToValue={(option, selectedValue) =>
                    option.id === selectedValue.id
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.companyName}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Enter customer name"
                      size="small"
                      error={!!error}
                      helperText={error?.message}
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'off',
                        readOnly: isReadOnly,
                      }}
                      sx={customerNameFieldSx}
                    />
                  )}
                />
              )}
            />
          </Box>
          <TextInputField
            label="Address"
            name="address"
            placeholder="Enter address"
            layout="horizontal"
          />
          <TextInputField
            label="Load#"
            name="loadNumber"
            placeholder="Enter load#"
            layout="horizontal"
          />
          <TextInputField
            label="Invoice & POD Email"
            name="billingEmail"
            placeholder="Enter invoice & POD email"
            layout="horizontal"
          />
          <TextInputField
            label="Notification Email"
            name="notificationEmail"
            placeholder="Enter notification email"
            layout="horizontal"
          />
          <TextInputField
            label="AP Email"
            name="accountPayableEmail"
            placeholder="Enter AP email"
            layout="horizontal"
          />
          <TextInputField
            label="Phone"
            name="phone"
            placeholder="Enter phone"
            layout="horizontal"
          />
          <TextInputField
            label="Other"
            name="other"
            placeholder="Enter other information"
            layout="horizontal"
          />
        </Stack>
      </Box>
    )
  }

  return (
    <LoadConfirmFormContainer container spacing={2}>
      <FormSingleColumnLayout>
        <Stack spacing={1}>
          <InputLabel sx={{ color: 'error.main', fontWeight: 700 }}>
            Customer Name
          </InputLabel>
          <Controller
            name="customerName"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                freeSolo
                disabled={isReadOnly}
                disableClearable={isReadOnly}
                options={isReadOnly ? [] : companySuggestions}
                loading={!isReadOnly && companySuggestionsLoading}
                value={null}
                inputValue={value || ''}
                sx={isReadOnly ? readOnlyAutocompleteSx : undefined}
                onInputChange={(_, inputValue, reason) => {
                  if (isReadOnly || reason === 'reset') return
                  onChange(inputValue)
                  if (reason === 'clear' || inputValue === '') {
                    handleCompanyCleared()
                    return
                  }
                  if (reason === 'input') {
                    handleCompanyCleared()
                  }
                }}
                onChange={(_, option) => {
                  if (isReadOnly) return
                  if (!option || typeof option === 'string') {
                    onChange(typeof option === 'string' ? option : '')
                    if (!option) {
                      handleCompanyCleared()
                    }
                    return
                  }
                  onChange(option.companyName)
                  handleCompanyChosen(option)
                }}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.companyName
                }
                isOptionEqualToValue={(option, selectedValue) =>
                  option.id === selectedValue.id
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.companyName}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Apotex Inc"
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'off',
                      readOnly: isReadOnly,
                    }}
                    sx={customerNameFieldSx}
                  />
                )}
              />
            )}
          />
        </Stack>
        <TextInputField label="Address" name="address" />
        <TextInputField label="Load#" name="loadNumber" highlight />
        <TextInputField
          label="Invoice & POD Email"
          name="billingEmail"
          highlight
        />
        <TextInputField label="Notification Email" name="notificationEmail" />
        <TextInputField label="AP Email" name="accountPayableEmail" />
        <TextInputField label="Phone" name="phone" />
        <TextInputField label="Other" name="other" />
      </FormSingleColumnLayout>
    </LoadConfirmFormContainer>
  )
}

export default LoadCustomerForm
